/**
 * Scenario list shown in the Flow Designer Configure tab beneath the inputs editor.
 *
 * Scenarios are named sets of input values used as parameterized run parameters.
 * The panel lists scenarios with add/edit/delete, a default toggle, and a warning
 * when a scenario references an input that no longer exists on the workflow.
 *
 * @module FlowDesigner/components/ScenarioListPanel
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Button, Card, EmptyState, Flex, Heading, TagBadge, Text } from '@chatsdet/ui';
import { ListChecks, PencilSimple, Plus, Trash, Warning } from '@phosphor-icons/react';
import type { WorkflowInputDefinition, WorkflowScenario } from '@chatsdet/types';
import { ScenarioEditor } from './ScenarioEditor';
import type { CredentialTemplate } from '../../../../hooks/useCredentialTemplates';

interface ScenarioListPanelProps {
  inputs: WorkflowInputDefinition[];
  scenarios: WorkflowScenario[];
  onSave: (scenarios: WorkflowScenario[]) => void;
  isSaving?: boolean;
  credentialTemplates?: CredentialTemplate[];
}

function makeEmptyScenario(): WorkflowScenario {
  return { id: crypto.randomUUID(), name: '' };
}

function findOrphanKeys(
  scenario: WorkflowScenario,
  inputs: WorkflowInputDefinition[]
): string[] {
  const valueNames = new Set(inputs.filter((d) => d.kind === 'value').map((d) => d.name));
  const credNames = new Set(inputs.filter((d) => d.kind === 'credential').map((d) => d.name));
  const orphans: string[] = [];
  for (const name of Object.keys(scenario.values ?? {})) {
    if (!valueNames.has(name)) orphans.push(name);
  }
  for (const name of Object.keys(scenario.credentials ?? {})) {
    if (!credNames.has(name)) orphans.push(name);
  }
  return orphans;
}

export const ScenarioListPanel: React.FC<ScenarioListPanelProps> = ({
  inputs,
  scenarios,
  onSave,
  isSaving,
  credentialTemplates,
}) => {
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [drafting, setDrafting] = React.useState<WorkflowScenario | null>(null);

  const startAdd = () => {
    const draft = makeEmptyScenario();
    setDrafting(draft);
    setEditingId(draft.id);
  };

  const cancel = () => {
    setDrafting(null);
    setEditingId(null);
  };

  const saveScenario = (scenario: WorkflowScenario) => {
    // If the saved scenario is marked default, clear default on the others.
    const next = scenarios.filter((s) => s.id !== scenario.id).map((s) =>
      scenario.isDefault && s.isDefault ? { ...s, isDefault: undefined } : s
    );
    const existingIdx = scenarios.findIndex((s) => s.id === scenario.id);
    if (existingIdx === -1) {
      next.push(scenario);
    } else {
      next.splice(existingIdx, 0, scenario);
    }
    onSave(next);
    cancel();
  };

  const deleteScenario = (id: string) => {
    onSave(scenarios.filter((s) => s.id !== id));
    if (editingId === id) cancel();
  };

  // If we're editing a new draft, surface it in the list so the user sees the editor inline.
  const displayScenarios = React.useMemo(() => {
    if (drafting && !scenarios.some((s) => s.id === drafting.id)) {
      return [...scenarios, drafting];
    }
    return scenarios;
  }, [scenarios, drafting]);

  return (
    <div className="space-y-4">
      {displayScenarios.length > 0 && (
        <Flex justify="between" align="center">
          <div>
            <Heading level={5} className="mb-0.5 font-semibold tracking-tight">
              Scenarios
            </Heading>
            <Text size="sm" className="text-muted-foreground">
              {scenarios.length} saved · scenarios marked{' '}
              <span className="font-medium text-foreground">In suite</span> run
              automatically when this workflow is part of a test suite
            </Text>
          </div>
          <Button
            size="sm"
            variant="outline"
            onPress={startAdd}
            isDisabled={!!editingId}
            iconLeft={<Plus size={14} />}
          >
            Add scenario
          </Button>
        </Flex>
      )}

      {displayScenarios.length === 0 ? (
        <EmptyState
          icon={<ListChecks size={22} />}
          title="No scenarios yet"
          description="Save a reusable set of input values to run this workflow against."
          action={
            <Button
              variant="primary"
              size="sm"
              onPress={startAdd}
              iconLeft={<Plus size={14} />}
            >
              Add scenario
            </Button>
          }
        />
      ) : (
        <div className="space-y-2">
          {displayScenarios.map((scenario, index) => {
            const orphans = findOrphanKeys(scenario, inputs);
            const isEditing = editingId === scenario.id;
            const initial =
              isEditing && drafting && drafting.id === scenario.id ? drafting : scenario;

            if (isEditing) {
              return (
                <ScenarioEditor
                  key={scenario.id}
                  inputs={inputs}
                  initial={initial}
                  isSaving={isSaving}
                  onSave={saveScenario}
                  onCancel={cancel}
                  credentialTemplates={credentialTemplates}
                />
              );
            }

            const valueCount = Object.keys(scenario.values ?? {}).length;
            const credentialCount = Object.keys(scenario.credentials ?? {}).length;
            const metaParts: string[] = [];
            if (valueCount > 0) {
              metaParts.push(`${valueCount} value${valueCount === 1 ? '' : 's'}`);
            }
            if (credentialCount > 0) {
              metaParts.push(
                `${credentialCount} credential${credentialCount === 1 ? '' : 's'}`
              );
            }

            return (
              <motion.div
                key={scenario.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18, delay: Math.min(index * 0.025, 0.2) }}
              >
                <Card
                  padding="sm"
                  className="group transition-colors hover:border-border"
                >
                <Flex justify="between" align="center" gap="sm">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Text size="sm" className="font-semibold truncate">
                        {scenario.name || 'Untitled scenario'}
                      </Text>
                      {scenario.isDefault && (
                        <TagBadge name="Default" color="blue" size="sm" hideDot />
                      )}
                      {scenario.includeInSuite !== false && (
                        <TagBadge name="In suite" color="purple" size="sm" hideDot />
                      )}
                    </div>
                    {scenario.description && (
                      <Text size="xs" className="text-muted-foreground truncate">
                        {scenario.description}
                      </Text>
                    )}
                    {metaParts.length > 0 && (
                      <Text size="xs" className="mt-0.5 text-muted-foreground/80">
                        {metaParts.join(' · ')}
                      </Text>
                    )}
                  </div>
                  <Flex gap="xs" className="opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100">
                    <Button
                      variant="icon"
                      size="sm"
                      iconOnly
                      tooltip="Edit scenario"
                      onPress={() => {
                        setDrafting(null);
                        setEditingId(scenario.id);
                      }}
                      iconLeft={<PencilSimple size={14} />}
                    />
                    <Button
                      variant="icon"
                      size="sm"
                      iconOnly
                      tooltip="Delete scenario"
                      onPress={() => deleteScenario(scenario.id)}
                      iconLeft={<Trash size={14} />}
                    />
                  </Flex>
                </Flex>

                  {orphans.length > 0 && (
                    <div className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-700 dark:text-amber-300">
                      <Warning size={12} weight="fill" className="mt-0.5 shrink-0" />
                      <span>
                        References missing input{orphans.length > 1 ? 's' : ''}:{' '}
                        {orphans.join(', ')}
                      </span>
                    </div>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};
